import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewContainerRef,
} from '@angular/core';
import { dynamicElementsButtonKey } from '@epicuro-next/constants/dynamic-elements';
import { DynamicElementsTooltipDirective } from '@epicuro-next/dynamic-elements/dynamic-tooltip';
import { Operation } from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { dispatchDynamicEvent } from '@epicuro-next/platform/dynamic-actions';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { LogicOperationsService } from '@epicuro-next/platform/dynamic-logical-operations';
import { isNullish } from '@epicuro-next/utilities';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@DynamicComponentRegister(dynamicElementsButtonKey, true)
@Component({
  selector: 'epc-dynamic-elements-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dynamic-elements-button.component.html',
  styleUrls: ['./dynamic-elements-button.component.scss'],
  hostDirectives: [
    {
      directive: DynamicElementsTooltipDirective,
      // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
      inputs: ['tooltip', 'tooltipPlacement'],
    },
  ],
  standalone: false,
})
export class DynamicElementsButtonComponent implements OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private disabledSubscription: Subscription | null = null;
  private destroy$ = new Subject<void>();
  private disabled$ = new BehaviorSubject(false);
  private vcRef = inject(ViewContainerRef);
  private clickCounter = 0;

  @HostBinding('class')
  public btnClass = 'btn';

  @Input()
  public text: string | null = null;

  @Input()
  public icon: string[] | null = null;

  @Input()
  public iconConfig: { placement: 'prefix' | 'suffix' } = {
    placement: 'prefix',
  };

  @Input()
  public stopEventPropogation = true;

  @Input()
  public action: string | { type: string; value: unknown } | null = null;

  @Input()
  public set disabled(value: Operation | boolean) {
    if (isNullish(value)) return;

    if (typeof value === 'boolean') {
      this.disabled$.next(value);
      return;
    }

    if (this.disabledSubscription) {
      this.disabledSubscription.unsubscribe();
      this.disabledSubscription = null;
    }

    this.disabledSubscription = this.lo
      .performOperationCheck(value, this.vcRef.injector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((operationResult) => {
        this.disabled$.next(operationResult);
        this.cdr.markForCheck();
      });
  }

  @Output()
  public isClicked = new EventEmitter<{ nrOfTimesClicked: number }>();

  @HostBinding('class.disabled')
  private get isDisabled() {
    return this.disabled$.getValue();
  }

  constructor(private lo: LogicOperationsService) {}

  @HostListener('click', ['$event'])
  public onClick(event: MouseEvent) {
    this.clickCounter++;
    this.isClicked.emit({ nrOfTimesClicked: this.clickCounter });

    if (!this.action || this.isDisabled) {
      return;
    }

    // doesn't play well with atxClickOutside
    if (this.stopEventPropogation) {
      event.stopPropagation();
    }

    if (typeof this.action === 'string') {
      dispatchDynamicEvent(this.action);

      return;
    }

    try {
      const { type, value } = this.action;

      dispatchDynamicEvent(type, value);
    } catch {
      throw new Error(
        `Failed to parse dynamic action ${JSON.stringify(this.action)}`,
      );
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
